import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { useToast } from "../hooks/use-toast";
import { adminLogin, enrollMfa, TOKEN_KEY } from "../admin/adminApi";
import ResourceManager from "../admin/ResourceManager";
import BookingsPanel from "../admin/BookingsPanel";
import { Lock, LogOut, CalendarDays, Scissors, Package, Tag, Users, LayoutGrid, FileText, Newspaper, Image as ImageIcon, ShieldCheck } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("password"); // password | setup | totp
  const [setupToken, setSetupToken] = useState("");
  const [uri, setUri] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const finish = (token) => { localStorage.setItem(TOKEN_KEY, token); onLogin(token); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await adminLogin(password, step === "totp" && code ? code : undefined);
      if (res.data.token) { finish(res.data.token); return; }
      if (res.data.mfa_setup_required) {
        setSetupToken(res.data.setup_token);
        const en = await enrollMfa(res.data.setup_token);
        setUri(en.data.otpauth_uri);
        setStep("setup");
        return;
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "totp_required") { setStep("totp"); setError(""); }
      else if (detail === "Invalid authenticator code") { setStep("totp"); setError("Galat code. App ka current 6-digit code daalein."); }
      else { setError("Incorrect password"); toast({ title: "Incorrect password" }); }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/40 px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl shadow-lg ring-1 ring-black/5 p-8">
        <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
          {step === "password" ? <Lock className="w-6 h-6" /> : <ShieldCheck className="w-6 h-6" />}
        </div>
        <h1 className="font-display text-2xl font-bold text-center mt-4 text-gray-900">Admin Login</h1>
        <p className="text-sm text-gray-500 text-center mt-1">BeBlushify Dashboard</p>

        {step !== "setup" && (
          <div className="mt-6 space-y-2">
            <Label>Password</Label>
            <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter admin password" autoFocus disabled={step === "totp"} />
          </div>
        )}

        {step === "setup" && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 mb-3">Security ke liye Google Authenticator app se ye QR code scan karein, phir <b>Continue</b> dabayen.</p>
            <div className="flex justify-center bg-white ring-1 ring-black/10 rounded-xl p-4" data-testid="mfa-qr"><QRCodeSVG value={uri || "about:blank"} size={180} /></div>
          </div>
        )}

        {step === "totp" && (
          <div className="mt-5 space-y-2">
            <Label>Authenticator Code</Label>
            <Input data-testid="admin-totp-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" className="text-center tracking-[0.4em] text-lg" autoFocus />
          </div>
        )}

        {error && <p role="alert" className="text-sm text-red-600 text-center mt-3">{error}</p>}

        <Button type="submit" data-testid="admin-login-submit" disabled={loading || (step === "totp" && code.length !== 6)} className="w-full mt-5 bg-primary hover:bg-primary/90 h-11">
          {loading ? "Please wait..." : step === "setup" ? "Continue" : step === "totp" ? "Verify & Sign In" : "Sign In"}
        </Button>
        {step === "setup" && <p className="text-xs text-center text-gray-400 mt-3">QR scan karne ke baad app mein jo 6-digit code dikhe wo next screen par daalna hoga.</p>}
        {step === "password" && <p className="text-xs text-center text-gray-400 mt-4">Protected by 2-step verification.</p>}
      </form>
    </div>
  );
};

const serviceFields = [
  { key: "name", label: "Service Name", type: "text" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "price", label: "Price (₹)", type: "number" },
  { key: "duration", label: "Duration (e.g. 45 min)", type: "text" },
  { key: "category", label: "Category", type: "select", optionsEndpoint: "categories" },
  { key: "subcategory", label: "Sub-category (e.g. Rica Waxing)", type: "text" },
  { key: "image", label: "Image", type: "image" },
  { key: "order", label: "Display Order (lower = shown first)", type: "number" },
  { key: "most_booked", label: "Add to Most Booked", type: "switch" },
  { key: "on_offer", label: "Running an Offer?", type: "switch" },
  { key: "offer_type", label: "Offer Type", type: "select", options: ["percent", "flat"] },
  { key: "offer_value", label: "Offer Value (% or ₹)", type: "number" },
  { key: "active", label: "Active", type: "switch" },
];
const packageFields = [
  { key: "name", label: "Package Name", type: "text" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "services", label: "Included Services", type: "multiselect", optionsEndpoint: "services", autoPriceField: "original_price" },
  { key: "original_price", label: "Original Price (auto from services, editable)", type: "number" },
  { key: "price", label: "Package Price (₹)", type: "number" },
  { key: "image", label: "Image", type: "image" },
  { key: "order", label: "Display Order (lower = shown first)", type: "number" },
  { key: "active", label: "Active", type: "switch" },
];
const bannerFields = [
  { key: "title", label: "Banner Title (e.g. Summer Festival Offer)", type: "text" },
  { key: "subtitle", label: "Subtitle / Description", type: "textarea" },
  { key: "image", label: "Banner Image", type: "image" },
  { key: "services", label: "Combo Services (optional - for combo deal)", type: "multiselect", optionsEndpoint: "services", autoPriceField: "original_price" },
  { key: "original_price", label: "Original Price (auto from services)", type: "number" },
  { key: "combo_price", label: "Combo Offer Price (₹) - leave 0 for text-only banner", type: "number" },
  { key: "order", label: "Display Order", type: "number" },
  { key: "active", label: "Active", type: "switch" },
];
const offerFields = [
  { key: "title", label: "Offer Title", type: "text" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "code", label: "Coupon Code", type: "text", placeholder: "e.g. FIRST20" },
  { key: "discount_type", label: "Discount Type", type: "select", options: ["percent", "flat"] },
  { key: "discount_value", label: "Discount Value", type: "number" },
  { key: "active", label: "Active", type: "switch" },
];
const beauticianFields = [
  { key: "name", label: "Name", type: "text" },
  { key: "phone", label: "WhatsApp Number (with country code, e.g. 9172...)", type: "text" },
  { key: "location", label: "Location", type: "text" },
  { key: "active", label: "Active", type: "switch" },
];
const categoryFields = [
  { key: "name", label: "Category Name", type: "text" },
  { key: "image", label: "Image", type: "image" },
  { key: "order", label: "Display Order", type: "number" },
  { key: "active", label: "Active", type: "switch" },
];
const pageFields = [
  { key: "slug", label: "Slug (url, e.g. faq)", type: "text" },
  { key: "title", label: "Page Title", type: "text" },
  { key: "content", label: "Content", type: "textarea" },
  { key: "image", label: "Banner Image", type: "image" },
  { key: "active", label: "Active", type: "switch" },
];
const blogFields = [
  { key: "title", label: "Blog Title", type: "text" },
  { key: "category", label: "Category", type: "text" },
  { key: "date", label: "Date (e.g. January 24, 2025)", type: "text" },
  { key: "excerpt", label: "Short Excerpt", type: "textarea" },
  { key: "content", label: "Full Content", type: "textarea" },
  { key: "image", label: "Image", type: "image" },
  { key: "active", label: "Active", type: "switch" },
];

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); };

  if (!token) return <LoginScreen onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-display text-2xl font-extrabold text-primary">BeBlushify</span>
            <Badge className="bg-primary/10 text-primary hover:bg-primary/10">Admin</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={logout}><LogOut className="w-4 h-4 mr-1.5" /> Logout</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Tabs defaultValue="bookings">
          <TabsList className="bg-white ring-1 ring-black/5 flex-wrap h-auto">
            <TabsTrigger value="bookings"><CalendarDays className="w-4 h-4 mr-1.5" /> Bookings</TabsTrigger>
            <TabsTrigger value="services"><Scissors className="w-4 h-4 mr-1.5" /> Services</TabsTrigger>
            <TabsTrigger value="packages"><Package className="w-4 h-4 mr-1.5" /> Packages</TabsTrigger>
            <TabsTrigger value="offers"><Tag className="w-4 h-4 mr-1.5" /> Offers</TabsTrigger>
            <TabsTrigger value="categories"><LayoutGrid className="w-4 h-4 mr-1.5" /> Categories</TabsTrigger>
            <TabsTrigger value="pages"><FileText className="w-4 h-4 mr-1.5" /> Pages</TabsTrigger>
            <TabsTrigger value="blog"><Newspaper className="w-4 h-4 mr-1.5" /> Blog</TabsTrigger>
            <TabsTrigger value="banners"><ImageIcon className="w-4 h-4 mr-1.5" /> Banners</TabsTrigger>
            <TabsTrigger value="beauticians"><Users className="w-4 h-4 mr-1.5" /> Beauticians</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6"><BookingsPanel /></TabsContent>
          <TabsContent value="services" className="mt-6">
            <ResourceManager title="Services" endpoint="services" fields={serviceFields}
              empty={{ name: "", description: "", price: 0, duration: "", category: "General", subcategory: "", image: "", order: 0, most_booked: false, on_offer: false, offer_type: "percent", offer_value: 0, active: true }} />
          </TabsContent>
          <TabsContent value="packages" className="mt-6">
            <ResourceManager title="Packages" endpoint="packages" fields={packageFields}
              empty={{ name: "", description: "", price: 0, original_price: 0, services: [], image: "", order: 0, active: true }} />
          </TabsContent>
          <TabsContent value="offers" className="mt-6">
            <ResourceManager title="Offers" endpoint="offers" fields={offerFields}
              empty={{ title: "", description: "", code: "", discount_type: "percent", discount_value: 0, active: true }} />
          </TabsContent>
          <TabsContent value="categories" className="mt-6">
            <ResourceManager title="Categories" endpoint="categories" fields={categoryFields}
              empty={{ name: "", image: "", order: 0, active: true }} />
          </TabsContent>
          <TabsContent value="pages" className="mt-6">
            <ResourceManager title="Pages" endpoint="pages" fields={pageFields}
              empty={{ slug: "", title: "", content: "", image: "", active: true }} />
          </TabsContent>
          <TabsContent value="blog" className="mt-6">
            <ResourceManager title="Blog Posts" endpoint="blogs" fields={blogFields}
              empty={{ title: "", category: "Beauty", date: "", excerpt: "", content: "", image: "", active: true }} />
          </TabsContent>
          <TabsContent value="banners" className="mt-6">
            <ResourceManager title="Banners" endpoint="banners" fields={bannerFields}
              empty={{ title: "", subtitle: "", image: "", services: [], original_price: 0, combo_price: 0, order: 0, active: true }} />
          </TabsContent>
          <TabsContent value="beauticians" className="mt-6">
            <ResourceManager title="Beauticians" endpoint="beauticians" fields={beauticianFields}
              empty={{ name: "", phone: "", location: "", active: true }} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Admin;
